import { BookUser, Link2, Plus, Search } from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { CallPager, CallRangeLabel } from "@/features/call-report";
import { ContactFormDialog } from "@/features/contact-form";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LEAD_STATES, leadStateText } from "@/lib/campaign";
import {
  SORTS, VIEWS, activeView, apiFilters, applyView, contactName, dialDetail, dialText,
  filtersFromParams, isFiltered, nextText, paramsFromFilters, stageSlugs, stageText, stripStages,
  toggleStage,
  type ContactFilters,
} from "@/lib/contacts";
import { humanize } from "@/lib/lead-card";
import {
  CONTACTS_PAGE_SIZE, useBulkContacts, useCallDispositions, useContactSummary, useContacts, useFirms,
} from "@/lib/queries";
import { atLeast, type ContactRow, type ContactSummary } from "@/lib/types";
import { cn, formatDateTime, relativeTime } from "@/lib/utils";
import { useDebounced } from "@/lib/use-debounced";
import { DialNumberButton } from "@/softphone/dial-number";

/**
 * The contacts list (ADR-044): one list, every firm.
 *
 * Every filter is in the URL, so a view is a link and Copy link copies it. The stage strip and the
 * chips are filters too — one tile lit is one stage; a chip is a filter set — and the tiles' counts come
 * from `/leads/summary`, which runs the same `WHERE` as the table with no `LIMIT`, so they describe the
 * filter and not the page. Two facts sit side by side on every row and are never merged: the stage
 * is the person's; the Dial column is one campaign's list row, the dialer's.
 */
export function ContactsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const supervisor = atLeast(user?.role, "supervisor");
  const [params, setParams] = useSearchParams();
  const filters = React.useMemo(() => filtersFromParams(params), [params]);
  const page = Math.max(0, Number.parseInt(params.get("page") ?? "0", 10) || 0);
  const [search, setSearch] = React.useState(filters.q ?? "");
  const debounced = useDebounced(search, 250);
  const [creating, setCreating] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  const update = React.useCallback(
    (next: ContactFilters) => {
      // Any filter change puts the reader back on the first page: page 4 of a narrower list is
      // usually empty, and an empty table reads as "no contacts" rather than "no page 4".
      setParams(paramsFromFilters(next));
    },
    [setParams],
  );

  // The typed search reaches the URL once it settles; a URL that changes under it (Back) wins.
  React.useEffect(() => {
    if ((debounced.trim() || undefined) !== filters.q) update({ ...filters, q: debounced.trim() || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  React.useEffect(() => {
    setSearch((current) => (current.trim() === (filters.q ?? "") ? current : (filters.q ?? "")));
  }, [filters.q]);

  const setPage = (next: number) => {
    const p = new URLSearchParams(params);
    if (next > 0) p.set("page", String(next));
    else p.delete("page");
    setParams(p);
  };

  const sent = React.useMemo(() => apiFilters(filters), [filters]);
  const list = useContacts(sent, page);
  const summary = useContactSummary(sent);
  const firms = useFirms();
  const bulk = useBulkContacts();
  // The catalog's words for an outcome's slug: the tenant's, which every campaign picks from.
  const catalog = useCallDispositions(null);
  const outcomeLabel = React.useCallback(
    (slug: string) => catalog.data?.find((d) => d.slug === slug)?.label ?? humanize(slug) ?? slug,
    [catalog.data],
  );

  React.useEffect(() => setSelected(new Set()), [sent, page]);

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? null;
  const from = page * CONTACTS_PAGE_SIZE;
  const filtered = isFiltered(filters);
  const view = activeView(filters);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied", "Anyone who opens it sees this view.");
    } catch {
      toast.error("Could not copy", "Copy the address bar instead.");
    }
  };

  const setFirmOnSelected = (firmId: number) => {
    const ids = [...selected];
    bulk.mutate(
      { ids, action: "set_firm", firm_id: firmId },
      {
        onSuccess: (result) => {
          const firm = firms.data?.find((f) => f.id === firmId);
          toast.success(
            `Firm set on ${result.applied} contact${result.applied === 1 ? "" : "s"}`,
            [
              firm ? firm.label : null,
              result.unchanged ? `${result.unchanged} already so` : null,
              result.skipped.length ? `${result.skipped.length} not in your reach` : null,
            ].filter(Boolean).join(" · "),
          );
          setSelected(new Set());
        },
        onError: (e) => toast.error("Could not set the firm", e instanceof ApiError ? e.message : "Please try again."),
      },
    );
  };

  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));

  return (
    <Page>
      <PageHeader
        title="Contacts"
        description={
          <>
            {summary.data ? `${summary.data.total.toLocaleString()} contacts · ` : ""}
            one list, every firm · the stage is the person's, the dial state is the campaign's
          </>
        }
        action={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            New contact
          </Button>
        }
      />

      <StageStrip summary={summary.data} loading={summary.isPending} lit={filters.stage ?? null} onToggle={(stage) => update(toggleStage(filters, stage))} />

      <Card className="overflow-hidden">
        <Toolbar>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone or email"
            leading={<Search />}
            className="w-64"
            aria-label="Search contacts"
          />
          <Select value={filters.firm ?? "all"} onValueChange={(v) => update({ ...filters, firm: v === "all" ? undefined : v })}>
            <SelectTrigger className="w-44" aria-label="Filter by firm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any firm</SelectItem>
              <SelectItem value="none">No firm</SelectItem>
              {(firms.data ?? []).map((firm) => (
                <SelectItem key={firm.id} value={firm.code}>
                  {firm.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.stage && !filters.stage.includes(",") ? filters.stage : filters.stage_group ? `group:${filters.stage_group}` : "all"}
            onValueChange={(v) => {
              const next: ContactFilters = { ...filters };
              delete next.stage;
              delete next.stage_group;
              if (v.startsWith("group:")) next.stage_group = v.slice(6);
              else if (v !== "all") next.stage = v;
              update(next);
            }}
          >
            <SelectTrigger className="w-44" aria-label="Filter by stage">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any stage</SelectItem>
              <SelectItem value="group:working">Working</SelectItem>
              <SelectItem value="group:scheduled">Booked</SelectItem>
              <SelectItem value="group:exit">Exits</SelectItem>
              {stageSlugs().map((stage) => (
                <SelectItem key={stage} value={stage}>
                  {stageText(stage).label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.language ?? "all"} onValueChange={(v) => update({ ...filters, language: v === "all" ? undefined : v })}>
            <SelectTrigger className="w-32" aria-label="Filter by language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any language</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Spanish</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.dial_state ?? "all"} onValueChange={(v) => update({ ...filters, dial_state: v === "all" ? undefined : v })}>
            <SelectTrigger className="w-40" aria-label="Filter by dial state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any dial state</SelectItem>
              {LEAD_STATES.map((state) => (
                <SelectItem key={state} value={state}>
                  {leadStateText(state).label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={`${filters.sort ?? "created"}:${filters.order ?? "desc"}`}
            onValueChange={(v) => {
              const [sort, order] = v.split(":");
              update({ ...filters, sort, order });
            }}
          >
            <SelectTrigger className="w-40" aria-label="Sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SORTS).flatMap(([key, label]) => [
                <SelectItem key={`${key}:desc`} value={`${key}:desc`}>{label} ↓</SelectItem>,
                <SelectItem key={`${key}:asc`} value={`${key}:asc`}>{label} ↑</SelectItem>,
              ])}
            </SelectContent>
          </Select>
          {filtered && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(""); update({}); }}>
              Clear
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => void copyLink()}>
            <Link2 />
            Copy link
          </Button>
          <span className="ml-auto flex items-center gap-2 text-xs text-text-muted">
            {list.isFetching && !list.isPending && <Spinner className="size-3" />}
            <CallRangeLabel from={from} count={rows.length} total={total} />
          </span>
        </Toolbar>

        <div className="flex flex-wrap gap-1.5 border-b border-border px-3 py-2" role="group" aria-label="Views">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              aria-pressed={view === v.key}
              onClick={() => update(applyView(filters, v))}
              className={cn(
                "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                view === v.key
                  ? "border-accent-8 bg-accent-2 text-accent-11"
                  : "border-border bg-surface text-text-muted hover:bg-surface-hover",
              )}
            >
              {v.label}
              {summary.data && <span className="ml-1 tabular-nums opacity-70">{v.count(summary.data).toLocaleString()}</span>}
            </button>
          ))}
        </div>

        {supervisor && selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-accent-1 px-3 py-2 text-xs text-text dark:bg-gray-2">
            <span className="font-medium">{selected.size} selected</span>
            <span className="text-text-muted">· Set firm</span>
            <Select onValueChange={(v) => setFirmOnSelected(Number(v))} disabled={bulk.isPending}>
              <SelectTrigger className="h-7 w-48" aria-label="Set firm on the selected contacts">
                <SelectValue placeholder="Choose a firm…" />
              </SelectTrigger>
              <SelectContent>
                {(firms.data ?? []).map((firm) => (
                  <SelectItem key={firm.id} value={String(firm.id)}>
                    {firm.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {bulk.isPending && <Spinner className="size-3" />}
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </div>
        )}

        {list.isPending ? (
          <TableSkeleton cols={9} rows={8} />
        ) : list.error ? (
          <ErrorState
            error={list.error}
            title="Could not load the contacts"
            action={
              <Button variant="outline" onClick={() => void list.refetch()}>
                Try again
              </Button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={BookUser}
            title={filtered ? "No contacts match" : "No contacts yet"}
            description={
              filtered
                ? "Try another firm or stage, or clear the filters."
                : "A contact arrives from intake, an import or a trigger, or is typed in here."
            }
            action={
              filtered ? (
                <Button variant="outline" onClick={() => { setSearch(""); update({}); }}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {supervisor && (
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        aria-label="Select every contact on this page"
                        className="accent-accent-9"
                        checked={allOnPage}
                        onChange={(e) =>
                          setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())
                        }
                      />
                    </TableHead>
                  )}
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Firm</TableHead>
                  <TableHead>State / Lang</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Last outcome</TableHead>
                  <TableHead>Next</TableHead>
                  <TableHead>Dial</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <ContactLine
                    key={row.id}
                    row={row}
                    outcomeLabel={outcomeLabel}
                    selectable={supervisor}
                    selected={selected.has(row.id)}
                    onSelect={(on) =>
                      setSelected((current) => {
                        const next = new Set(current);
                        if (on) next.add(row.id);
                        else next.delete(row.id);
                        return next;
                      })
                    }
                  />
                ))}
              </TableBody>
            </Table>
            </div>
            <CallPager
              page={page}
              count={rows.length}
              total={total}
              onChange={setPage}
              busy={list.isFetching}
              pageSize={CONTACTS_PAGE_SIZE}
            />
          </>
        )}
      </Card>

      <ContactFormDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(card) => navigate(`/contacts/${card.id}`)}
      />
    </Page>
  );
}

/** The tiles across the top: the working stages and the win, each a filter. */
function StageStrip({
  summary, loading, lit, onToggle,
}: {
  summary: ContactSummary | undefined;
  loading: boolean;
  lit: string | null;
  onToggle: (stage: string) => void;
}) {
  const counts = new Map(summary?.by_stage.map((s) => [s.stage, s]));
  return (
    <div className="mb-4 grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2" role="group" aria-label="Stages">
      {stripStages().map((stage) => {
        const text = stageText(stage);
        const count = counts.get(stage);
        const hint =
          stage === "signed_pending_intake"
            ? summary ? `${summary.signed_this_month} this month` : ""
            : count ? (count.due_now ? `${count.due_now.toLocaleString()} due now` : "none due") : "";
        return (
          <button
            key={stage}
            type="button"
            aria-pressed={lit === stage}
            title={text.description}
            onClick={() => onToggle(stage)}
            className={cn(
              "rounded-lg border bg-surface px-3 py-2.5 text-left shadow-xs transition-colors hover:bg-surface-hover",
              lit === stage ? "border-accent-8 ring-2 ring-accent-8/25" : "border-border",
            )}
          >
            <p className="truncate text-2xs font-medium uppercase tracking-wide text-text-subtle">
              {stage === "signed_pending_intake" && text.label === "Signed, pending intake" ? "Signed · pending" : text.label}
            </p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-text">
              {loading || !summary ? <span className="text-text-subtle">—</span> : (count?.count ?? 0).toLocaleString()}
            </p>
            <p className="truncate text-xs text-text-muted">{hint || " "}</p>
          </button>
        );
      })}
    </div>
  );
}

function ContactLine({
  row, outcomeLabel, selectable, selected, onSelect,
}: {
  row: ContactRow;
  outcomeLabel: (slug: string) => string;
  selectable: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
}) {
  const name = contactName(row);
  const stage = stageText(row.stage);
  const dial = dialText(row.dial);
  const detail = dialDetail(row);
  const next = nextText(row);
  return (
    <TableRow className={cn(selected && "bg-accent-1 dark:bg-gray-2")}>
      {selectable && (
        <TableCell>
          <input
            type="checkbox"
            aria-label={`Select ${name ?? row.phone}`}
            className="accent-accent-9"
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
          />
        </TableCell>
      )}
      <TableCell className="whitespace-nowrap">
        <Link to={`/contacts/${row.id}`} className="font-medium text-text hover:underline">
          {name ?? <span className="text-text-muted">No name</span>}
        </Link>
        {row.dnc && (
          <Badge tone="danger" size="sm" className="ml-2">
            DNC
          </Badge>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap font-mono text-xs">
        <DialNumberButton number={row.phone} name={name} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs">
        {row.firm ? (
          <span className="text-text">{row.firm.label}</span>
        ) : (
          <span className="text-warning-11">No firm</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-text-muted">
        {[row.state, row.language ? row.language.toUpperCase() : null].filter(Boolean).join(" · ") || "—"}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <Badge tone={stage.tone} size="sm" title={row.stage_since ? `Since ${formatDateTime(row.stage_since)}` : stage.description}>
          {stage.label}
        </Badge>
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-text">
        {row.disposition ? (
          <>
            {outcomeLabel(row.disposition)}
            {row.last_outcome_at && <span className="text-text-subtle"> · {relativeTime(row.last_outcome_at)}</span>}
          </>
        ) : (
          <span className="text-text-subtle">—</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-text-muted">{next ?? "—"}</TableCell>
      <TableCell className="whitespace-nowrap">
        <Badge tone={dial.tone} size="sm" title={dial.description}>
          {dial.label}
        </Badge>
        {detail && <span className="ml-1.5 text-xs text-text-subtle">{detail}</span>}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-text-muted" title={row.last_activity_at ? formatDateTime(row.last_activity_at) : undefined}>
        {row.last_activity_at ? relativeTime(row.last_activity_at) : "—"}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-text-muted" title={formatDateTime(row.created_at)}>
        {formatDateTime(row.created_at).split(",")[0]}
      </TableCell>
    </TableRow>
  );
}
